import { useEffect, useMemo, useState } from "react";
import { usePrevious } from "react-use";
import { skipToken } from "@reduxjs/toolkit/query";
import { useSnackbar } from "notistack";

import {
  apiSlice,
  useExecuteCustomWorkflowMutation,
  useGetCustomWorkflowExecutionsQuery,
  useGetCustomWorkflowsQuery,
  useGetVersionQuery,
} from "fond/api";
import { refreshTileSource } from "fond/map/redux";
import { useAppDispatch } from "fond/utils/hooks";

type Outputs = {
  isLoading: boolean;
  workflowStepIndex: number | undefined | null;
  workflowState: undefined | "IN_PROGRESS" | "COMPLETE" | "ERROR" | "PENDING" | "STARTING";
  workflowProgress: undefined | number;
  runWorkflow: null | ((parameters?: object | undefined) => void);
};

type IProps = {
  // The ID of the version the workflow is associated with.
  versionId: string | undefined;

  // The key of the custom workflow to observe and/or execute.
  key: string | undefined;

  // Called when the workflow is observed moving from an in-progress state to the complete state.
  onComplete?: () => void;
};

export function useCustomWorkflow({ versionId, key, onComplete }: IProps): Outputs {
  const [pollingInterval, setPollingInterval] = useState<number | null>(null);
  const { refetch: refetchVersion } = useGetVersionQuery(versionId ?? skipToken);
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { data: customWorkflowExecutions, isLoading: isLoadingWorkflows } = useGetCustomWorkflowExecutionsQuery(versionId ?? skipToken, {
    pollingInterval: pollingInterval !== null ? pollingInterval : 30000,
    skip: !key,
  });
  const { data: customWorkflows } = useGetCustomWorkflowsQuery(key ? versionId || skipToken : skipToken);
  const [executeCustomWorkflow, { isLoading, isSuccess }] = useExecuteCustomWorkflowMutation();

  const workflowExecution = useMemo(() => {
    return customWorkflowExecutions && customWorkflowExecutions.CustomWorkflowExecutions.find((execution) => execution.CustomWorkflow.Key === key);
  }, [customWorkflowExecutions, key]);
  const workflowState = useMemo(() => {
    if (isLoading || (isSuccess && !workflowExecution)) return "STARTING";
    return workflowExecution ? workflowExecution.Status.State : undefined;
  }, [isLoading, isSuccess, workflowExecution]);
  const workflowProgress = workflowExecution && workflowExecution.Status.Progress !== null ? workflowExecution.Status.Progress : undefined;
  const workflowStepIndex = workflowExecution?.Status.CurrentStepIndex;
  const previousWorkflowState = usePrevious(workflowState);

  // Get the custom workflow object
  const workflow = useMemo(() => {
    return customWorkflows && customWorkflows.CustomWorkflows.find((wf) => wf.Key === key);
  }, [customWorkflows, key]);

  // Run the custom workflow
  const runWorkflow = useMemo(() => {
    if (!workflow || !versionId) {
      return null;
    }

    return (parameters: object | undefined) => {
      executeCustomWorkflow({ versionId, workflowId: workflow.ID, parameters })
        .unwrap()
        .catch(() => {
          enqueueSnackbar("Operation failed. Please try again...", { variant: "error" });
        });
    };
  }, [workflow, executeCustomWorkflow, versionId, enqueueSnackbar]);

  useEffect(() => {
    // Trigger a refresh on the version if we observe the workflow move to the complete state from elsewhere
    if (previousWorkflowState && previousWorkflowState !== "COMPLETE" && workflowState === "COMPLETE") {
      dispatch(refreshTileSource("project-layers"));
      dispatch(
        apiSlice.util.invalidateTags([
          { type: "FeatureTotals", id: versionId },
          { type: "Layers", id: versionId },
        ])
      );
      refetchVersion();
      onComplete?.();
    }

    // Alter the polling interval dynamically based on whether the workflow is currently executing
    if (workflowState === "IN_PROGRESS") {
      setPollingInterval(5000);
    } else {
      setPollingInterval(null);
    }
  }, [dispatch, previousWorkflowState, refetchVersion, versionId, workflowState]);

  return { workflowState, workflowProgress, workflowStepIndex, runWorkflow, isLoading: isLoadingWorkflows || isLoading };
}
